// Copiato con microfix
// https://github.com/react-bootstrap/react-overlays/blob/v1.2.0/src/Affix.js
import classNames from 'classnames'
import getHeight from 'dom-helpers/query/height'
import getOffset from 'dom-helpers/query/offset'
import listen from 'dom-helpers/events/listen'
import getOffsetParent from 'dom-helpers/query/offsetParent'
import getScrollTop from 'dom-helpers/query/scrollTop'
import requestAnimationFrame from 'dom-helpers/util/requestAnimationFrame'
import PropTypes from 'prop-types'
import React from 'react'
import ReactDOM from 'react-dom'

import getDocumentHeight from './utils/getDocumentHeight'
import ownerDocument from './utils/ownerDocument'
import ownerWindow from './utils/ownerWindow'

/**
 * The `<Affix/>` component toggles `position: fixed;` on and off, emulating
 * the effect found with `position: sticky;`.
 */
class Affix extends React.Component {
  constructor(props, context) {
    super(props, context)

    this.state = {
      affixed: 'top',
      position: null,
      top: null,
    }
  }

  componentDidMount() {
    this._isMounted = true

    this.removeScrollListener = listen(ownerWindow(this), 'scroll', () =>
      this.onWindowScroll()
    )
    this.removeClickListener = listen(ownerDocument(this), 'click', () =>
      this.onDocumentClick()
    )

    this.onUpdate()
  }

  componentDidUpdate(prevProps) {
    if (prevProps !== this.props) {
      this.onUpdate()
    }
  }

  componentWillUnmount() {
    this._isMounted = false

    if (this.removeClickListener) this.removeClickListener()
    if (this.removeScrollListener) this.removeScrollListener()
  }

  onWindowScroll = () => {
    this.onUpdate()
  }

  onDocumentClick = () => {
    requestAnimationFrame(() => this.onUpdate())
  }

  onUpdate = () => {
    if (!this._isMounted) {
      return
    }

    const { offsetTop, viewportOffsetTop } = this.props
    const scrollTop = getScrollTop(ownerWindow(this))
    const positionTopMin = scrollTop + (viewportOffsetTop || 0)

    if (positionTopMin <= offsetTop) {
      this.updateState('top', null, null)
      return
    }

    if (positionTopMin > this.getPositionTopMax()) {
      if (this.state.affixed === 'bottom') {
        this.updateStateAtBottom()
      } else {
        // Setting position away from `fixed` can change the offset parent of
        // the affix, so we can't calculate the correct position until after
        // we've updated its position.
        this.setState(
          {
            affixed: 'bottom',
            position: 'absolute',
            top: null,
          },
          () => {
            if (!this._isMounted) {
              return
            }

            this.updateStateAtBottom()
          }
        )
      }
      return
    }

    this.updateState('affix', 'fixed', viewportOffsetTop)
  }

  getPositionTopMax = () => {
    const documentHeight = getDocumentHeight(ownerDocument(this))
    const height = getHeight(ReactDOM.findDOMNode(this))

    return documentHeight - height - this.props.offsetBottom
  }

  updateState = (affixed, position, top) => {
    if (
      affixed === this.state.affixed &&
      position === this.state.position &&
      top === this.state.top
    ) {
      return
    }

    let upperName =
      affixed === 'affix'
        ? ''
        : affixed.charAt(0).toUpperCase() + affixed.substr(1)

    if (this.props['onAffix' + upperName]) {
      this.props['onAffix' + upperName]()
    }

    this.setState({ affixed, position, top }, () => {
      if (this.props['onAffixed' + upperName]) {
        this.props['onAffixed' + upperName]()
      }
    })
  }

  updateStateAtBottom = () => {
    const positionTopMax = this.getPositionTopMax()
    const offsetParent = getOffsetParent(ReactDOM.findDOMNode(this))
    const parentTop = getOffset(offsetParent).top

    this.updateState('bottom', 'absolute', positionTopMax - parentTop)
  }

  render() {
    const child = React.Children.only(this.props.children)
    const { className, style } = child.props

    const { affixed, position, top } = this.state
    const positionStyle = { position, top }

    let affixClassName
    let affixStyle
    if (affixed === 'top') {
      affixClassName = this.props.topClassName
      affixStyle = this.props.topStyle
    } else if (affixed === 'bottom') {
      affixClassName = this.props.bottomClassName
      affixStyle = this.props.bottomStyle
    } else {
      affixClassName = this.props.affixClassName
      affixStyle = this.props.affixStyle
    }

    return React.cloneElement(child, {
      className: classNames(affixClassName, className),
      style: { ...positionStyle, ...affixStyle, ...style },
    })
  }
}

export const propTypes = {
  /**
   * Pixels to offset from top of screen when calculating position
   */
  offsetTop: PropTypes.number,

  /**
   * When affixed, pixels to offset from top of viewport
   */
  viewportOffsetTop: PropTypes.number,

  /**
   * Pixels to offset from bottom of screen when calculating position
   */
  offsetBottom: PropTypes.number,

  /**
   * CSS class or classes to apply when at top
   */
  topClassName: PropTypes.string,

  /**
   * Style to apply when at top
   */
  topStyle: PropTypes.object,

  /**
   * CSS class or classes to apply when affixed
   */
  affixClassName: PropTypes.string,

  /**
   * Style to apply when affixed
   */
  affixStyle: PropTypes.object,

  /**
   * CSS class or classes to apply when at bottom
   */
  bottomClassName: PropTypes.string,

  /**
   * Style to apply when at bottom
   */
  bottomStyle: PropTypes.object,

  /**
   * Callback fired right before the `affixStyle` and `affixClassName` props are rendered
   */
  onAffix: PropTypes.func,

  /**
   * Callback fired after the component `affixStyle` and `affixClassName` props have been rendered
   */
  onAffixed: PropTypes.func,

  /**
   * Callback fired right before the `topStyle` and `topClassName` props are rendered
   */
  onAffixTop: PropTypes.func,

  /**
   * Callback fired after the component `topStyle` and `topClassName` props have been rendered
   */
  onAffixedTop: PropTypes.func,

  /**
   * Callback fired right before the `bottomStyle` and `bottomClassName` props are rendered
   */
  onAffixBottom: PropTypes.func,

  /**
   * Callback fired after the component `bottomStyle` and `bottomClassName` props have been rendered
   */
  onAffixedBottom: PropTypes.func,
}

Affix.propTypes = propTypes;

Affix.defaultProps = {
  offsetTop: 0,
  viewportOffsetTop: null,
  offsetBottom: 0,
}

export default Affix;